import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { workflowApi } from "./workflow.api";
import { useWorkflowStore } from "./workflow.store";
import type { CreateWorkflowRequest, UpdateWorkflowRequest } from "./workflow.types";

export const workflowKeys = {
	all: ["workflows"] as const,
	list: (params?: { page?: number; limit?: number }) =>
		[...workflowKeys.all, "list", params?.page ?? 1, params?.limit ?? 20] as const,
	detail: (id: string) => [...workflowKeys.all, "detail", id] as const,
};

export const useGetWorkflows = (params?: { page?: number; limit?: number }) => {
	const setList = useWorkflowStore((state) => state.setList);

	const query = useQuery({
		queryKey: workflowKeys.list(params),
		queryFn: () => workflowApi.list(params),
	});

	useEffect(() => {
		if (query.data) {
			setList({
				list: query.data.workflows,
				total: query.data.total,
				page: query.data.page,
				limit: query.data.limit,
			});
		}
	}, [query.data, setList]);

	return query;
};

export const useGetWorkflow = (id: string) => {
	const setCurrent = useWorkflowStore((state) => state.setCurrent);

	const query = useQuery({
		queryKey: workflowKeys.detail(id),
		queryFn: () => workflowApi.get(id),
		enabled: Boolean(id),
	});

	useEffect(() => {
		if (query.data) setCurrent(query.data);
	}, [query.data, setCurrent]);

	return query;
};

export const useCreateWorkflow = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (request: CreateWorkflowRequest) => workflowApi.create(request),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: workflowKeys.all });
		},
	});
};

export const useUpdateWorkflow = () => {
	const queryClient = useQueryClient();
	const setCurrent = useWorkflowStore((state) => state.setCurrent);

	return useMutation({
		mutationFn: ({ id, request }: { id: string; request: UpdateWorkflowRequest }) =>
			workflowApi.update(id, request),
		onSuccess: (data) => {
			setCurrent(data);
			queryClient.invalidateQueries({ queryKey: workflowKeys.all });
			queryClient.setQueryData(workflowKeys.detail(data.id), data);
		},
	});
};

export const useDeleteWorkflow = () => {
	const queryClient = useQueryClient();
	const setCurrent = useWorkflowStore((state) => state.setCurrent);

	return useMutation({
		mutationFn: (id: string) => workflowApi.remove(id),
		onSuccess: () => {
			setCurrent(null);
			queryClient.invalidateQueries({ queryKey: workflowKeys.all });
		},
	});
};
